import { lessonSkillDisplay } from 'constants/lessonSkills/lessonSkills';
import { AccountType } from 'constants/user';
import {
  GetCoachAllPlayersReflectionsQuery,
  GetCoachReflectionsBySubmitterIdQuery,
  GetPlayerReflectionByPlayerIdQuery,
  LessonPlayerSkillAssessmentCategoriesEnum,
  LessonPlayerSkillAssessments,
  LessonPlayerSkillAssessmentsSkills,
  LessonReflectionCommentTypesEnum,
  UserProfiles,
  VideoAnalysisReviewStatusesEnum,
} from 'types/generated/client';
import { toSentenceCase } from 'utils/shared/string/toSentenceCase';
import {
  AvatarInfoType,
  FutureSkillsProps,
  MediaType,
  ProcessedConfidenceSkillsProps,
  ProcessedReflection,
  ReflectionDetail,
} from './props';

const categorizeSkills = (skills: LessonPlayerSkillAssessmentsSkills[]) => {
  return skills.reduce(
    (acc, skill) => {
      if (skill.category === LessonPlayerSkillAssessmentCategoriesEnum.Positive) {
        skill.skill && acc.positive.add(skill.skill);
      } else if (skill.category === LessonPlayerSkillAssessmentCategoriesEnum.Negative) {
        skill.skill && acc.negative.add(skill.skill);
      }
      return acc;
    },
    { positive: new Set<string>(), negative: new Set<string>() },
  );
};

const getTimeSortedSkills = (skills: LessonPlayerSkillAssessmentsSkills[]) => {
  const relevantSkills = skills.filter(
    (skill) =>
      skill.category === LessonPlayerSkillAssessmentCategoriesEnum.Positive ||
      skill.category === LessonPlayerSkillAssessmentCategoriesEnum.Negative,
  );

  return relevantSkills.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
};

const getMostRecentSkills = (
  skills: LessonPlayerSkillAssessmentsSkills[],
): ProcessedConfidenceSkillsProps['mostRecent'] => {
  // Filter only POSITIVE and NEGATIVE skills and sort by createdAt
  const sortedSkills = getTimeSortedSkills(skills);

  // Get the most recent positive skill (if it exists)
  const mostRecentPositive = sortedSkills.find(
    (skill) => skill.category === LessonPlayerSkillAssessmentCategoriesEnum.Positive,
  );

  // Get the most recent negative skill (if it exists)
  const mostRecentNegative = sortedSkills.find(
    (skill) => skill.category === LessonPlayerSkillAssessmentCategoriesEnum.Negative,
  );

  return {
    positive: mostRecentPositive ? [mostRecentPositive.skill] : [],
    negative: mostRecentNegative ? [mostRecentNegative.skill] : [],
  };
};
export const getGeneralSkills = (
  skills: LessonPlayerSkillAssessmentsSkills[],
): ProcessedConfidenceSkillsProps['general'] => {
  const categorized = categorizeSkills(skills);
  return {
    positive: Array.from(categorized.positive),
    negative: Array.from(categorized.negative),
  };
};

export const processLessonDataConfidence = (
  lessonData: LessonPlayerSkillAssessments[],
): ProcessedConfidenceSkillsProps => {
  const allSkills = lessonData.flatMap((lesson) => lesson.lessonPlayerSkillAssessmentsSkills);

  return {
    mostRecent: getMostRecentSkills(allSkills),
    general: getGeneralSkills(allSkills),
  };
};

export const getFutureSkills = (lessons: LessonPlayerSkillAssessments[]): FutureSkillsProps[] => {
  // Map each lesson to its relevant NEXT_GOAL skills and the common notedDate
  const futureSkillsData = lessons
    .map((lesson) => {
      const nextGoalSkills = lesson.lessonPlayerSkillAssessmentsSkills
        .filter((skill) => skill.category === LessonPlayerSkillAssessmentCategoriesEnum.NextGoal)
        .map((skill) => skill.skill)
        .filter(Boolean) as string[]; // Ensure we only take non-null skill names

      // If there are no NEXT_GOAL skills, skip this lesson
      if (nextGoalSkills.length === 0) return null;

      const notedDate = lesson.lessonPlayerSkillAssessmentsSkills[0].createdAt;

      return {
        lessonId: lesson.lessonId,
        id: lesson?.id,
        notedDate,
        skills: nextGoalSkills,
        coachAvatarInfo: {
          name: lesson?.lessonCoach?.fullName || '',
          imageSrc: lesson?.lessonCoach?.profileImageProviderUrl || '',
        },
      };
    })
    .filter(Boolean) as FutureSkillsProps[]; // Remove null entries

  // Sort by notedDate in descending order
  return futureSkillsData.sort(
    (a, b) => new Date(b.notedDate).getTime() - new Date(a.notedDate).getTime(),
  );
};

export const getSkillLabel = (str: string = '') => {
  const skillDisplay = lessonSkillDisplay.find((skill) => skill.id === str);
  return skillDisplay?.displayName || toSentenceCase(str.replaceAll('_', ' '));
};

export const processLessonReflections = (
  lessons:
    | GetPlayerReflectionByPlayerIdQuery['lessons']
    | GetCoachReflectionsBySubmitterIdQuery['lessons']
    | GetCoachAllPlayersReflectionsQuery['lessons'],
  flatten: boolean = false,
): ProcessedReflection[] => {
  const processLesson = (
    lesson: (typeof lessons)[0],
    reflection?: (typeof lesson)['lessonReflections'][0],
  ) => {
    const skills = new Set<string>();
    const completedSkills = new Set<string>();
    const upcomingSkills = new Set<string>();
    const reflectionDetails: ReflectionDetail[] = [];
    let media: MediaType | null = null;

    const comments =
      reflection?.lessonReflectionComments ||
      lesson.lessonReflections?.[0]?.lessonReflectionComments ||
      [];
    comments.forEach((comment) => {
      skills.add(comment.skill || comment.customSkill || '');
      comment.type === LessonReflectionCommentTypesEnum.Upcoming &&
        upcomingSkills.add(comment.skill || comment.customSkill || '');

      comment.type === LessonReflectionCommentTypesEnum.Completed &&
        completedSkills.add(comment.skill || comment.customSkill || '');

      reflectionDetails.push({
        skill: comment.skill || comment.customSkill || '',
        sentiment: comment.sentiment || '',
        detail: comment.details || '',
        type: comment.type,
        avatarInfo: {
          imageSrc: reflection?.lessonCoach?.profileImageProviderUrl || '',
          name: reflection?.lessonCoach?.fullName || '',
        },
        media: comment.lessonReflectionFiles.map((file) => ({
          src: file.url,
          type: file.fileType,
          format: file.fileFormat,
        }))?.[0],
      });
    });

    if (!media) {
      const primaryMedia = lesson.lessonReflections?.[0]?.files?.[0];
      if (primaryMedia) {
        media = {
          src: primaryMedia.url,
          type: primaryMedia.fileType,
          format: primaryMedia.fileFormat,
        };
      }
    }

    return {
      reflectionId: lesson.lessonReflections?.[0]?.id || reflection?.id || '',
      lessonId: lesson.id,
      skills: Array.from(skills),
      upcomingSkills: Array.from(upcomingSkills),
      completedSkills: Array.from(completedSkills),
      reflectionDetails,
      media,
      detail: lesson.lessonReflections?.[0]?.details || reflection?.details || '',
      upcomingCategory:
        lesson.lessonReflections?.[0]?.upcomingCategory || reflection?.upcomingCategory,
      upcomingSport: lesson.lessonReflections?.[0]?.upcomingSport || reflection?.upcomingSport,
      completedCategory:
        lesson.lessonReflections?.[0]?.completedCategory || reflection?.completedCategory,
      completedSport: lesson.lessonReflections?.[0]?.completedSport || reflection?.completedSport,
      date: lesson?.times?.[0]?.startDateTime || '',
      coachAvatarInfo: {
        imageSrc: lesson.ownerProfile?.profileImageProviderUrl || '',
        name: lesson.ownerProfile?.fullName || '',
      },
      playerAvatarInfo: {
        imageSrc: isAllPlayersLesson(lesson)
          ? lesson.participants?.[0].userProfile?.profileImageProviderUrl || ''
          : '',
        name: isAllPlayersLesson(lesson)
          ? lesson.participants?.[0].userProfile?.fullName || ''
          : '',
      },
      isCoachReflectionPending: lesson.lessonReflections.length === 0,
      isCoachRecapSkipped: lesson.participants?.[0]?.recapSkippedAt,
      isPlayerReflectionPending: isPlayerLesson(lesson)
        ? lesson.lessonPlayerSkillAssessments.length === 0
        : false,
      lessonOwner: (lesson?.ownerProfile as UserProfiles) || null,
      lessonTitle: lesson.title,
      lessonType: lesson.type,
      address: lesson.venue?.addressString || lesson.userCustomCourt?.fullAddress || '',
      player: (lesson.participants?.[0]?.userProfile as UserProfiles) || null,
    };
  };

  if (flatten && isAllPlayersLessons(lessons)) {
    return lessons.flatMap((lesson) => {
      const reviewerIds = lesson.lessonReflections.map((reflection) => reflection.lessonPlayer?.id);

      const filteredParticipants = lesson.participants.filter(
        (participant) => !reviewerIds.includes(participant.userProfile?.id),
      );

      return filteredParticipants.map((participant) => {
        const lessonClone = {
          ...lesson,
          participants: [participant],
          lessonReflections: [],
        };
        return processLesson(lessonClone);
      });
    });
  } else {
    return lessons.map((lesson) => processLesson(lesson));
  }
};

export const isPlayerLesson = (
  lesson: any,
): lesson is GetPlayerReflectionByPlayerIdQuery['lessons'][0] => {
  return !!lesson.lessonPlayerSkillAssessments;
};

export const isAllPlayersLessons = (
  lessons: any,
): lessons is GetCoachAllPlayersReflectionsQuery['lessons'] => !!lessons?.[0]?.participants;

export const isAllPlayersLesson = (
  lessons: any,
): lessons is GetCoachAllPlayersReflectionsQuery['lessons'][0] => !!lessons?.participants;

export const getSortedByPendingReflection = (
  reflections: ProcessedReflection[],
  accountType: AccountType,
  prioritizeNonPending: boolean = false,
): ProcessedReflection[] => {
  return reflections.sort((a, b) => {
    const isPendingA =
      accountType === AccountType.Coach ? a.isCoachReflectionPending : a.isPlayerReflectionPending;

    const isPendingB =
      accountType === AccountType.Coach ? b.isCoachReflectionPending : b.isPlayerReflectionPending;

    if (prioritizeNonPending) {
      if (!isPendingA && isPendingB) return -1;
      if (isPendingA && !isPendingB) return 1;
    } else {
      if (isPendingA && !isPendingB) return -1;
      if (!isPendingA && isPendingB) return 1;
    }

    // Sort by date descending for reflections with the same pending status
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
};
